import { useQuery } from '@tanstack/react-query';
import { dashboardApi, accountsApi, insightsApi, subscriptionsApi } from '@/services/api';
import { CHART_TOOLTIP, AXIS_TICK, compactUsd } from '@/components/charts/chartTheme';
import { TagChip } from '@/components/tags/TagChip';
import { useTags } from '@/components/tags/tagQueries';
import { monthLabel } from '@/utils/time';
import { formatCurrency } from '@tally/shared';
import { CATEGORY_MAP } from '@tally/shared';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import { Plane, Repeat, CalendarRange, TrendingUp, ArrowRight, Loader2, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/routes';
import { motion } from 'framer-motion';

export function DashboardPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => dashboardApi.summary(),
    refetchInterval: 30000,
  });

  const { data: syncData } = useQuery({
    queryKey: ['sync-status-dashboard'],
    queryFn: () => accountsApi.syncStatus(),
    refetchInterval: 5000,
  });

  const { data: allTime } = useQuery({ queryKey: ['insights', 'all'], queryFn: () => insightsApi.get({}) });
  const { data: subs } = useQuery({ queryKey: ['subscriptions'], queryFn: () => subscriptionsApi.list() });
  const tags = useTags();

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  const summary = data?.data;
  if (!summary) return null;

  const liveSync = syncData?.data?.overall;

  return (
    <div className="space-y-6">
      <BackfillBanner syncStatus={summary.syncStatus} liveProgress={liveSync} />

      <div>
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-sm text-muted-foreground">Your spending at a glance</p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatCard icon={TrendingUp} label="This month" value={formatCurrency(summary.thisMonthSpent)} color="text-amber-500" />
        <StatCard
          icon={CalendarRange}
          label="Last 12 months"
          value={formatCurrency(allTime?.data?.periods.find((p) => p.key === '1y')?.totalUsd ?? 0)}
          color="text-green-500"
          to={ROUTES.app.insights}
        />
        <StatCard
          icon={Repeat}
          label="Subscriptions / mo"
          value={formatCurrency(subs?.data?.summary.monthlyUsd ?? 0)}
          color="text-purple-500"
          to={ROUTES.app.subscriptions}
        />
        <StatCard icon={Plane} label="Trips" value={String(summary.totalTrips)} color="text-blue-500" to={ROUTES.app.trips} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-xl border border-border bg-card p-4 sm:p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm font-semibold">Last 12 months</h3>
            <Link to={ROUTES.app.insights} className="flex items-center gap-1 text-xs text-primary hover:underline">
              Insights <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={(allTime?.data?.byMonth ?? []).slice(-12).map((m) => ({ ...m, label: monthLabel(m.month) }))}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="label" tick={AXIS_TICK} minTickGap={16} />
                <YAxis tick={AXIS_TICK} tickFormatter={compactUsd} width={48} />
                <Tooltip {...CHART_TOOLTIP} formatter={(value: number, name: string) => [formatCurrency(value), name]} />
                <Area type="monotone" dataKey="otherUsd" stackId="1" stroke="#6366f1" fill="#6366f1" fillOpacity={0.3} name="Everyday" />
                <Area type="monotone" dataKey="tripUsd" stackId="1" stroke="#14b8a6" fill="#14b8a6" fillOpacity={0.3} name="On trips" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-4 sm:p-6">
          <h3 className="mb-4 text-sm font-semibold">Top categories</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={summary.topCategories.slice(0, 6).map((c: any) => ({
                    name: CATEGORY_MAP.get(c.category)?.label || c.category,
                    value: c.total,
                    color: CATEGORY_MAP.get(c.category)?.color || '#94a3b8',
                  }))}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  dataKey="value"
                  paddingAngle={2}
                >
                  {summary.topCategories.slice(0, 6).map((c: any, i: number) => (
                    <Cell key={i} fill={CATEGORY_MAP.get(c.category)?.color || '#94a3b8'} />
                  ))}
                </Pie>
                <Tooltip {...CHART_TOOLTIP} formatter={(value: number, name: string) => [formatCurrency(value), name]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 space-y-1">
            {summary.topCategories.slice(0, 5).map((c: any) => (
              <div key={c.category} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: CATEGORY_MAP.get(c.category)?.color }} />
                  <span className="text-muted-foreground">{CATEGORY_MAP.get(c.category)?.label || c.category}</span>
                </div>
                <span className="font-medium">{formatCurrency(c.total)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-4 sm:p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm font-semibold">Upcoming renewals</h3>
            <Link to={ROUTES.app.subscriptions} className="flex items-center gap-1 text-xs text-primary hover:underline">
              All subscriptions <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          {(() => {
            const upcoming = (subs?.data?.items ?? []).filter((s) => s.active).sort((a, b) => a.nextExpected.localeCompare(b.nextExpected)).slice(0, 5);
            return upcoming.length === 0 ? (
              <p className="text-sm text-muted-foreground">No subscriptions found yet.</p>
            ) : (
              <ul className="divide-y divide-border">
                {upcoming.map((s) => (
                  <li key={s.key} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{s.merchant}</span>
                      <span className="text-xs text-muted-foreground">~{s.nextExpected} · {s.cadence}</span>
                    </span>
                    <span className="shrink-0 font-semibold">{formatCurrency(s.typicalAmount, s.currency)}</span>
                  </li>
                ))}
              </ul>
            );
          })()}
        </div>

        <div className="rounded-xl border border-border bg-card p-4 sm:p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm font-semibold">Top tags</h3>
            <Link to={ROUTES.app.tags} className="flex items-center gap-1 text-xs text-primary hover:underline">
              All tags <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          {tags.length === 0 ? (
            <p className="text-sm text-muted-foreground">No tags yet. Tag expenses from Transactions, or let AI do it on the Tags page.</p>
          ) : (
            <ul className="divide-y divide-border">
              {[...tags].sort((a, b) => b.totalUsd - a.totalUsd).slice(0, 5).map((t) => (
                <li key={t.id}>
                  <Link to={`${ROUTES.app.transactions}?tagId=${encodeURIComponent(t.id)}`} className="flex items-center justify-between gap-3 py-2.5 text-sm hover:opacity-80">
                    <TagChip name={t.name} color={t.color} />
                    <span className="text-xs text-muted-foreground">{t.transactionCount} expenses</span>
                    <span className="ml-auto shrink-0 font-semibold">{formatCurrency(t.totalUsd)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-4 sm:p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm font-semibold">Recent trips</h3>
            <Link to={ROUTES.app.trips} className="flex items-center gap-1 text-xs text-primary hover:underline">
              View all <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="space-y-3">
            {summary.recentTrips.length === 0 ? (
              <p className="text-sm text-muted-foreground">No trips detected yet. Trips will appear once your data is synced.</p>
            ) : (
              summary.recentTrips.map((trip: any) => (
                <Link
                  key={trip.id}
                  to={ROUTES.app.trip(trip.id)}
                  className="flex items-center justify-between rounded-lg border border-border p-3 transition-colors hover:bg-accent"
                >
                  <div>
                    <p className="text-sm font-medium">{trip.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {trip.destination} &middot; {trip.travelerCount} traveler{trip.travelerCount !== 1 ? 's' : ''}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {trip.startDate?.split('T')[0]} — {trip.endDate?.split('T')[0]}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold">{formatCurrency(trip.totalExpenses)}</p>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-4 sm:p-6">
          <h3 className="mb-4 text-sm font-semibold">Sync status</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Connected accounts</span>
              <span className="text-sm font-medium">{summary.syncStatus.connectedAccounts}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Last synced</span>
              <span className="text-sm font-medium">
                {summary.syncStatus.lastSyncAt ? new Date(summary.syncStatus.lastSyncAt).toLocaleString() : 'Never'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Pending backfills</span>
              <span className="text-sm font-medium">{summary.syncStatus.pendingBackfills}</span>
            </div>
            {summary.syncStatus.errors > 0 && (
              <div className="rounded-lg bg-destructive/10 p-3">
                <p className="text-xs font-medium text-destructive">
                  {summary.syncStatus.errors} account(s) need attention
                </p>
              </div>
            )}
            <Link
              to={ROUTES.app.accounts}
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border border-border py-2 text-sm font-medium hover:bg-accent"
            >
              Manage accounts
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, to }: { icon: any; label: string; value: string; color: string; to?: string }) {
  const body = (
    <div className="flex items-center gap-3">
      <div className={`hidden h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent sm:flex ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <p className="truncate text-xs text-muted-foreground">{label}</p>
        <p className="truncate text-lg font-bold">{value}</p>
      </div>
    </div>
  );
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl border border-border bg-card p-4 sm:p-5">
      {to ? <Link to={to} className="block hover:opacity-80">{body}</Link> : body}
    </motion.div>
  );
}

function BackfillBanner({ syncStatus, liveProgress }: { syncStatus: any; liveProgress?: any }) {
  const isSyncing = syncStatus.pendingBackfills > 0 || liveProgress?.isSyncing;

  if (isSyncing) {
    const emailsScanned = liveProgress?.emailsScanned || 0;
    const transactionsFound = liveProgress?.transactionsFound || 0;
    const tripsDetected = liveProgress?.tripsDetected || 0;

    return (
      <div className="flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 px-5 py-4">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
        <div className="flex-1">
          <p className="text-sm font-medium">Syncing your email...</p>
          <p className="text-xs text-muted-foreground">
            {emailsScanned} emails scanned &middot; {transactionsFound} expenses found {tripsDetected > 0 ? `· ${tripsDetected} trips detected` : ''}
          </p>
        </div>
      </div>
    );
  }

  if (syncStatus.connectedAccounts > 0 && syncStatus.lastSyncAt) {
    return null;
  }

  if (syncStatus.connectedAccounts === 0) {
    return (
      <Link to={ROUTES.app.accounts} className="flex items-center gap-3 rounded-xl border border-amber-300/50 bg-amber-50 px-5 py-4 dark:border-amber-800/50 dark:bg-amber-950/30">
        <div className="flex-1">
          <p className="text-sm font-medium">Connect your accounts to get started</p>
          <p className="text-xs text-muted-foreground">
            Link bank accounts and email to automatically track expenses and detect trips.
          </p>
        </div>
        <ArrowRight className="h-4 w-4 text-muted-foreground" />
      </Link>
    );
  }

  return null;
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-8 w-48 animate-pulse rounded bg-muted" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-24 animate-pulse rounded-xl bg-muted" />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 h-80 animate-pulse rounded-xl bg-muted" />
        <div className="h-80 animate-pulse rounded-xl bg-muted" />
      </div>
    </div>
  );
}
